/**
 * Cells for enrichment job rows.
 *
 * Two consumers:
 *
 * 1. `useItemsColumns({ variant: "enrichment" })` — the enrichment column set,
 *    which replaces `site`/`url`/`context`/`current_stage` (all constant or
 *    meaningless on a job row: `site` is always `system-enrichment`, `url` is
 *    always null) with entity / mode / source / attempts / parks.
 * 2. The **default** column set's URL cell, which used to render the literal
 *    text "discover-seed" for every `url === null` row — i.e. for every
 *    enrichment job, on `/tina/items`, `/tina/hitl` and `/tina/runs/show`
 *    simultaneously. `ItemUrlFallback` tests what the row actually is.
 */
import { Badge } from "@/components/ui/badge";
import { makeTooltip } from "@/components/ui/tooltip";
import {
  contentTypeLabel,
  isEnrichmentContentType,
} from "@/lib/tina/content-types";
import {
  enrichmentEntityHref,
  enrichmentEntityLabel,
  enrichmentModeLabel,
  enrichmentSourceLabel,
  readEnrichmentJobRef,
  readEnrichmentParkState,
} from "@/lib/tina/enrichment-jobs";
import { isDiscoverSeedItem } from "@/pages/tina/items/item-context";
import type { PipelineItem } from "@/types/tina";

const dash = <span className="text-muted-foreground">—</span>;

/**
 * The entity a job row is about: label badge + truncated id, linking to the
 * entity's own IMS page in a new tab. Falls back to the content-type label when
 * the metadata block is unreadable, so a malformed row still says what it is.
 */
export function EnrichmentEntityCell({ item }: { item: PipelineItem }) {
  const ref = readEnrichmentJobRef(item);
  if (!ref) {
    return (
      <span className="text-xs text-muted-foreground">
        {contentTypeLabel(item.content_type)}
      </span>
    );
  }
  const href = enrichmentEntityHref(ref.entity_type, ref.entity_id);
  const body = (
    <span className="flex min-w-0 items-center gap-1.5">
      <Badge variant="secondary" className="text-[10px] font-normal">
        {enrichmentEntityLabel(ref.entity_type)}
      </Badge>
      <span className="block max-w-[150px] truncate font-mono text-[11px] text-muted-foreground">
        {ref.entity_id}
      </span>
    </span>
  );
  return makeTooltip(
    href ? (
      <a
        href={href}
        target="_blank"
        rel="noreferrer"
        onClick={(e) => e.stopPropagation()}
        className="block min-w-0 hover:underline"
      >
        {body}
      </a>
    ) : (
      body
    ),
    ref.entity_id,
  );
}

export function EnrichmentModeCell({ item }: { item: PipelineItem }) {
  const ref = readEnrichmentJobRef(item);
  if (!ref?.mode) return dash;
  return (
    <Badge variant="outline" className="text-[10px] font-normal">
      {enrichmentModeLabel(ref.mode)}
    </Badge>
  );
}

export function EnrichmentSourceCell({ item }: { item: PipelineItem }) {
  const ref = readEnrichmentJobRef(item);
  if (!ref?.source) return dash;
  return makeTooltip(
    <span className="block max-w-[160px] truncate text-xs">
      {enrichmentSourceLabel(ref.source)}
    </span>,
    ref.requested_by
      ? `${ref.source} · requested by ${ref.requested_by}`
      : ref.source,
  );
}

/** `attempt_count`, amber from the second attempt on. */
export function AttemptsCell({ item }: { item: PipelineItem }) {
  const attempts = item.attempt_count ?? 0;
  return (
    <span
      className={`font-mono text-xs ${
        attempts >= 2 ? "text-amber-700 dark:text-amber-400" : ""
      }`}
    >
      {attempts}
    </span>
  );
}

/**
 * `park_count` against the park cap. A job that hit the cap closed itself
 * `manual_review` and will never park again, which is the state a curator has to
 * be able to see without opening the row.
 *
 * A `null` count is a row with no park history at all, and a dash says so —
 * `0/3` would assert that the job reached a verdict and passed it.
 */
export function ParksCell({ item }: { item: PipelineItem }) {
  const parks = readEnrichmentParkState(item);
  if (parks.count == null && !parks.limitReached) return dash;
  const text = `${parks.count ?? 0}/${parks.limit}`;
  if (!parks.limitReached) {
    return <span className="font-mono text-xs">{text}</span>;
  }
  return makeTooltip(
    <span className="font-mono text-xs text-destructive">{text} · closed</span>,
    "Park limit reached — the job closed itself for manual review and will not park again.",
  );
}

/**
 * What the URL column shows when there is no URL.
 *
 * `url === null` now means three different things: a discover
 * seed (the original case), an enrichment job (every one of them), and the
 * genuinely-unknown remainder. Rendering "discover-seed" for all three is wrong
 * on two of them.
 */
export function ItemUrlFallback({ item }: { item: PipelineItem }) {
  const ref = readEnrichmentJobRef(item);
  if (ref) return <EnrichmentEntityCell item={item} />;
  // A job row whose `metadata.enrichment` is unreadable is still not a discover
  // seed, and neither is a tenth `enrichment_*` type Tina grows after this
  // deploy — which `isEnrichmentContentType` (a closed nine-value tuple) would
  // not recognise. The prefix test is the deliberate widening for that case.
  if (
    isEnrichmentContentType(item.content_type) ||
    item.content_type.startsWith("enrichment_")
  ) {
    return (
      <em className="text-xs text-muted-foreground">
        {contentTypeLabel(item.content_type)}
      </em>
    );
  }
  if (isDiscoverSeedItem(item)) {
    return <em className="text-xs text-muted-foreground">discover-seed</em>;
  }
  return dash;
}
